/**
 * Semantic layout components for accessibility
 * These wrap content in proper HTML5 landmarks for screen readers
 */

import React from 'react'

interface LayoutProps {
  children: React.ReactNode
  className?: string
  id?: string
}

interface MainProps extends LayoutProps {
  'aria-labelledby'?: string
}

/**
 * Main content area - should be used once per page
 * Provides <main> landmark for screen readers
 */
export function Main({ children, className, id, 'aria-labelledby': labelledby }: MainProps) {
  return (
    <main
      id={id}
      className={className}
      aria-labelledby={labelledby}
    >
      {children}
    </main>
  )
}

interface NavProps extends LayoutProps {
  'aria-label': string
}

/**
 * Navigation landmark
 * Requires aria-label to distinguish from other nav elements
 */
export function Nav({ children, className, 'aria-label': label }: NavProps) {
  return (
    <nav className={className} aria-label={label}>
      {children}
    </nav>
  )
}

interface SectionProps extends LayoutProps {
  'aria-labelledby'?: string
  'aria-label'?: string
}

/**
 * Section landmark - for distinct content areas
 */
export function Section({ children, className, id, 'aria-labelledby': labelledby, 'aria-label': label }: SectionProps) {
  return (
    <section
      id={id}
      className={className}
      aria-labelledby={labelledby}
      aria-label={label}
    >
      {children}
    </section>
  )
}

interface ArticleProps extends LayoutProps {
  'aria-labelledby'?: string
}

/**
 * Article landmark - for self-contained content (fixtures, bets, etc.)
 */
export function Article({ children, className, id, 'aria-labelledby': labelledby }: ArticleProps) {
  return (
    <article
      id={id}
      className={className}
      aria-labelledby={labelledby}
    >
      {children}
    </article>
  )
}

/**
 * Header landmark - for page/section headers
 */
export function Header({ children, className }: LayoutProps) {
  return (
    <header className={className}>
      {children}
    </header>
  )
}

/**
 * Footer landmark - for page/section footers
 */
export function Footer({ children, className }: LayoutProps) {
  return (
    <footer className={className}>
      {children}
    </footer>
  )
}

/**
 * Skip link component for keyboard navigation
 * Place at the very top of the page
 */
interface SkipLinkProps {
  targetId: string
  children?: React.ReactNode
}

export function SkipLink({ targetId, children = 'Skip to main content' }: SkipLinkProps) {
  return (
    <a href={`#${targetId}`} className="skip-link">
      {children}
    </a>
  )
}

/**
 * Live region for screen reader announcements
 * Use for dynamic content updates
 */
interface LiveRegionProps {
  children: React.ReactNode
  politeness?: 'polite' | 'assertive' | 'off'
  atomic?: boolean
  className?: string
}

export function LiveRegion({
  children,
  politeness = 'polite',
  atomic = true,
  className
}: LiveRegionProps) {
  return (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic={atomic}
      className={className || 'sr-only'}
    >
      {children}
    </div>
  )
}

/**
 * Visually hidden text for screen readers
 * Use for providing context that's visually apparent but not to screen readers
 */
export function VisuallyHidden({ children }: { children: React.ReactNode }) {
  return <span className="sr-only">{children}</span>
}
